import { FC, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { PinIcon } from 'lucide-react';
import ReactDOMServer from 'react-dom/server';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import { API_BASE_URL } from '@/constants';

import { fromLonLat } from 'ol/proj';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import Icon from 'ol/style/Icon';
import Style from 'ol/style/Style';
import Cluster from 'ol/source/Cluster';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';

const MapSkeleton = () => (
  <div className="h-64 w-full animate-pulse rounded-lg bg-gray-200" />
);

export const HomeMapPreview: FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: pets, isLoading } = useQuery({
    queryKey: ['pets-map-preview'],
    queryFn: async (): Promise<Array<Pet>> => {
      const response = await axios.get(`${API_BASE_URL}/pets/`, {
        params: {
          is_active: true,
          solved_case: false,
          no_pagination: true,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!mapRef.current || !pets) return;

    const newMap = new Map({
      target: mapRef.current,
      controls: [],
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat([-60.7, -32.9468]),
        zoom: 11,
      }),
    });

    const features = pets.map(
      (pet) =>
        new Feature({
          geometry: new Point(fromLonLat([pet.location.lon, pet.location.lat])),
          petId: pet.id,
        }),
    );

    const clusterSource = new Cluster({
      distance: 40,
      source: new VectorSource({ features }),
    });

    const clusterLayer = new VectorLayer({
      source: clusterSource,
      style: (feature) => {
        const size = feature.get('features').length;
        if (size > 1) {
          return new Style({
            image: new CircleStyle({
              radius: 15,
              fill: new Fill({ color: '#1a1a1a' }),
            }),
            text: new Text({
              text: size.toString(),
              fill: new Fill({ color: '#ffffff' }),
              font: '700 12px "Archivo Black", sans-serif',
            }),
          });
        }
        const petFeature = feature.get('features')[0];
        const pet = pets.find((p) => p.id === petFeature.get('petId'));
        const markerColor = pet?.status === 'lost' ? '#f5f0a8' : '#d9c8f5';
        const pinIconSVG = ReactDOMServer.renderToString(
          <PinIcon fill={markerColor} color="#1a1a1a" size={32} strokeWidth={2.5} />,
        );
        return new Style({
          image: new Icon({
            anchor: [0.5, 1],
            src: `data:image/svg+xml;base64,${btoa(pinIconSVG)}`,
          }),
        });
      },
    });

    newMap.addLayer(clusterLayer);

    newMap.on('singleclick', (event) => {
      newMap.forEachFeatureAtPixel(event.pixel, (feature) => {
        const features = feature.get('features');
        if (features?.length === 1) {
          navigate(`/pet/${features[0].get('petId')}`);
        }
      });
    });

    return () => {
      newMap.setTarget(undefined);
    };
  }, [pets, navigate]);

  if (isLoading) return <MapSkeleton />;

  return <div ref={mapRef} className="h-64 w-full overflow-hidden rounded-lg" />;
};
